import { Dialog } from "radix-ui";
import { VisuallyHidden } from "radix-ui";
import {
  createContext,
  forwardRef,
  ReactNode,
  RefObject,
  useRef,
  useState,
} from "react";
import CloseIcon from "../icons/CloseIcon";
import Draggable from "react-draggable";
import { ErrorBoundary } from "react-error-boundary";
import Button from "@/components/bricks/button/Button";

interface IModalProps {
  title?: ReactNode;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /**
   * If set, user cannot dismiss modal by clicking outside content,
   * hitting the Escape button, and the close button is hidden.
   */
  dialog?: boolean;
  onErrorReset?: () => void;
}

export const ModalRoot = (props: {
  children: any;
  controller?: {
    open: boolean;
    setOpen: (open: boolean) => void;
  };
}) => {
  const { children, controller } = props;
  if (controller) {
    return (
      <Dialog.Root open={controller.open} onOpenChange={controller.setOpen}>
        {children}
      </Dialog.Root>
    );
  }
  return <Dialog.Root>{children}</Dialog.Root>;
};

interface IModalTriggerProps {
  disabled?: boolean;
  children?: ReactNode;
  asChild?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}
export const ModalTrigger = forwardRef<HTMLButtonElement, IModalTriggerProps>(
  (props: IModalTriggerProps, ref) => {
    return (
      <Dialog.Trigger
        disabled={props.disabled}
        ref={ref}
        className={props.className}
        asChild={props.asChild}
        onClick={props.onClick}
      >
        {props.children}
      </Dialog.Trigger>
    );
  },
);

export const Modal = (props: IModalProps) => {
  const {
    title,
    description,
    children,
    footer,
    dialog,
    className,
    onErrorReset,
  } = props;
  const dragRef = useRef<HTMLDivElement>(null);
  return (
    <Dialog.Portal>
      <Dialog.Overlay className="c-modal-overlay">
        <Draggable
          handle=".draggable-handle"
          bounds="body"
          defaultPosition={{ x: 0, y: 71 }}
          nodeRef={dragRef as RefObject<HTMLElement>}
        >
          <Dialog.Content
            ref={dragRef}
            className={`c-modal ${className ?? ""}`}
            onInteractOutside={(e) => {
              if (dialog) e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              if (dialog) e.preventDefault();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <ErrorBoundary
              FallbackComponent={ModalErrorFallback}
              onReset={onErrorReset}
            >
              <header className="c-modal-header draggable-handle">
                <Dialog.Title className="c-modal-title">{title}</Dialog.Title>
                {!dialog && (
                  <Dialog.Close className="c-modal-close btn btn--icon btn--transparent btn-secondary">
                    <CloseIcon />
                  </Dialog.Close>
                )}
                {description && (
                  <VisuallyHidden.Root asChild>
                    <Dialog.Description>{description}</Dialog.Description>
                  </VisuallyHidden.Root>
                )}
              </header>
              <div className="c-modal-content">{children}</div>
              {footer && <div className="c-modal-footer">{footer}</div>}
            </ErrorBoundary>
          </Dialog.Content>
        </Draggable>
      </Dialog.Overlay>
    </Dialog.Portal>
  );
};

const ModalErrorFallback = (props: {
  error: unknown;
  resetErrorBoundary?: () => void;
}) => {
  const message =
    props.error instanceof Error
      ? props.error.message
      : String(props.error ?? "");
  return (
    <>
      <header className="c-modal-header draggable-handle">
        <Dialog.Title className="c-modal-title">Runtime error</Dialog.Title>
      </header>
      <div className="c-modal-content">
        <div className="c-modal-container">
          {" "}
          <div className="c-modal-container-inner">
            <p>Noget gik galt i modalen</p>
            {message && <p>{message}</p>}
          </div>
          <div className="c-modal-footer">
            {props.resetErrorBoundary && (
              <Button color="primary" onClick={props.resetErrorBoundary}>
                Tilbage
              </Button>
            )}
            {/* Always offer a way out: "dialog" modals hide the close button,
                and Tilbage re-renders the same state, which can throw again. */}
            <Dialog.Close asChild>
              <Button color="secondary">Luk</Button>
            </Dialog.Close>
          </div>
        </div>
      </div>
    </>
  );
};

export const ModalContext = createContext<{
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}>({
  modalOpen: false,
  setModalOpen: () => {},
});

export const ModalProvider = (props: { children: ReactNode }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  return (
    <ModalContext.Provider value={{ modalOpen, setModalOpen }}>
      <ModalRoot controller={{ open: modalOpen, setOpen: setModalOpen }}>
        {props.children}
      </ModalRoot>
    </ModalContext.Provider>
  );
};
export default Modal;
